/**
 * Legal documents (Privacy Policy, Terms of Service, AI Disclosure, Account
 * Deletion): the admin-editable content contract shared by the API, the admin
 * console editor, and the public marketing renderer.
 *
 * Content is a closed union of blocks that maps 1:1 onto the presentational
 * helpers in `apps/web/src/components/marketing/sections/legal/LegalBlocks.tsx`,
 * so a document authored in the admin console renders identically to the
 * hand-written TSX it replaced. Rich text inside a block uses the constrained
 * inline syntax parsed by `@tabbio/utils` (`parseLegalInline`) — never HTML.
 *
 * Runtime const arrays (LEGAL_DOCUMENT_KEYS, …) are re-exported through
 * `legal.runtime.js` so nodenext ESM consumers resolve them: import the values
 * from `@tabbio/types` (which points at the `.runtime.js`) and the types here.
 *
 * Mirrors the Prisma models in `packages/db/prisma/schema/legal.prisma`.
 */

// ── Enumerations (const arrays back the unions; see legal.runtime.js) ─────────

export const LEGAL_DOCUMENT_KEYS = [
  "privacy_policy",
  "terms_of_service",
  "ai_disclosure",
  "account_deletion",
] as const;

export type LegalDocumentKey = (typeof LEGAL_DOCUMENT_KEYS)[number];

export const LEGAL_DOCUMENT_SLUGS = [
  "privacy-policy",
  "terms-of-service",
  "ai-disclosure",
  "account-deletion",
] as const;

export type LegalDocumentSlug = (typeof LEGAL_DOCUMENT_SLUGS)[number];

/** Public route segment for each document, used for URLs and cache tags. */
export const LEGAL_DOCUMENT_SLUG_BY_KEY: Record<
  LegalDocumentKey,
  LegalDocumentSlug
> = {
  privacy_policy: "privacy-policy",
  terms_of_service: "terms-of-service",
  ai_disclosure: "ai-disclosure",
  account_deletion: "account-deletion",
};

export const LEGAL_VERSION_STATUSES = [
  "draft",
  "published",
  "archived",
] as const;

export type LegalVersionStatus = (typeof LEGAL_VERSION_STATUSES)[number];

export const LEGAL_LOCALES = ["en", "ar"] as const;

export type LegalLocale = (typeof LEGAL_LOCALES)[number];

export const LEGAL_BLOCK_TYPES = [
  "paragraph",
  "list",
  "callout",
  "caps",
  "cards",
  "subsection",
] as const;

export type LegalBlockType = (typeof LEGAL_BLOCK_TYPES)[number];

// ── Blocks ───────────────────────────────────────────────────────────────────

/** Body copy. Renders through `<P>`. */
export type LegalParagraphBlock = { type: "paragraph"; text: string };

/** Bulleted list. Renders through `<UL>` with one `<li>` per item. */
export type LegalListBlock = { type: "list"; items: string[] };

/** Emphasised aside with a bolded lead-in label. Renders through `<Callout>`. */
export type LegalCalloutBlock = { type: "callout"; label: string; text: string };

/** All-caps statutory notice (liability caps, disclaimers). `<LegalCaps>`. */
export type LegalCapsBlock = { type: "caps"; text: string };

/** Definition cards, e.g. the "what we collect" grid. `<CardGrid>`. */
export type LegalCardsBlock = {
  type: "cards";
  items: { term: string; def: string }[];
};

/**
 * A titled group of blocks inside a numbered section. Nesting is intentionally
 * capped at one level: every existing use is one level deep, and a flat cap
 * keeps both the schema finite and the editor legible.
 */
export type LegalSubsectionBlock = {
  type: "subsection";
  title: string;
  blocks: LegalLeafBlock[];
};

/** Any block that may appear inside a subsection. */
export type LegalLeafBlock =
  | LegalParagraphBlock
  | LegalListBlock
  | LegalCalloutBlock
  | LegalCapsBlock
  | LegalCardsBlock;

/** Any block that may appear at the top level of a section. */
export type LegalBlock = LegalLeafBlock | LegalSubsectionBlock;

// ── Documents ────────────────────────────────────────────────────────────────

/** One numbered clause. `id` is the TOC anchor (e.g. "s1"). */
export type LegalSectionContent = {
  id: string;
  num: number;
  title: string;
  blocks: LegalBlock[];
};

export type LegalDocumentContent = { sections: LegalSectionContent[] };

/** Per-locale bullets describing what changed; reused as the email summary. */
export type LegalChangeSummary = Record<LegalLocale, string[]>;

export type LegalLocalizedContent = Record<LegalLocale, LegalDocumentContent>;

/** Shape returned by `GET /api/public/legal/:key` for a single locale. */
export type PublicLegalDocument = {
  key: LegalDocumentKey;
  slug: LegalDocumentSlug;
  locale: LegalLocale;
  version: number;
  /** ISO-8601. The public page renders this as the document's date stamp. */
  effectiveDate: string;
  publishedAt: string | null;
  content: LegalDocumentContent;
};

// ── Admin views ──────────────────────────────────────────────────────────────

export type LegalVersionSummary = {
  id: string;
  version: number;
  status: LegalVersionStatus;
  effectiveDate: string;
  changeSummary: LegalChangeSummary;
  publishedAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string | null; email: string } | null;
  publishedBy: { id: string; name: string | null; email: string } | null;
  /** Set once a policy-update campaign has been created for this version. */
  notifyCampaignId: string | null;
};

export type LegalDocumentSummary = {
  key: LegalDocumentKey;
  slug: LegalDocumentSlug;
  label: string;
  publishedVersion: LegalVersionSummary | null;
  draftVersion: LegalVersionSummary | null;
};

export type LegalDocumentDetail = LegalDocumentSummary & {
  published: LegalLocalizedContent | null;
  draft: LegalLocalizedContent | null;
};

/** Recipient arithmetic shown in the notify confirmation dialog. */
export type LegalNotifyPreview = {
  totalUsers: number;
  eligible: number;
  skippedSystemEmailDisabled: number;
  /** Hard ceiling for a single system notice campaign. */
  maximumAudience: number;
  /** True when `eligible` exceeds what one campaign may carry. */
  exceedsMaximum: boolean;
};
